import { c as _c } from "react/compiler-runtime";
import React, { Children, PropsWithChildren, UIEvent, cloneElement, isValidElement, useMemo, useCallback, useRef, useState } from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet as SheetComponent, SheetRef } from 'react-modal-sheet';
import { useKey } from 'react-use';
import CloseIcon from '@/assets/icons/heroIcons/Cross.svg';
import { mergeClasses } from '@/utils/style';
export interface UseModalOpts {
  snapPoints?: number[];
  withCloseIcon?: boolean;
  title?: string;
  header?: JSX.Element;
  onCloseCallback?: () => void;
  onCloseEnd?: () => void;
  disableAutoClose?: boolean;
  disableAutoFocus?: boolean;
  closeOnSelect?: boolean;
}
export const useSheet = (render, opts) => {
  const $ = _c(10);
  const [isOpen, setIsOpen] = useState(false);
  const [dynamicComponentProps, setDynamicComponentProps] = useState();
  const closeCallback = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = dynamicProps => {
      setDynamicComponentProps(dynamicProps);
      return new Promise(resolve => {
        setIsOpen(true);
        closeCallback.current = resolve;
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const openSheet = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => new Promise(resolve_0 => {
      setIsOpen(false);
      closeCallback.current = resolve_0;
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hideSheet = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      closeCallback.current && closeCallback.current();
      setIsOpen(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onCloseSheet = t2;
  const memoizedOpts = opts;
  const t3 = memoizedOpts.snapPoints;
  let t4;
  if ($[3] !== dynamicComponentProps || $[4] !== render) {
    t4 = render(dynamicComponentProps);
    $[3] = dynamicComponentProps;
    $[4] = render;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isOpen || $[7] !== memoizedOpts || $[8] !== t4) {
    const sheet = <Sheet {...memoizedOpts} snapPoints={t3} isOpen={isOpen} onClose={onCloseSheet}>{t4}</Sheet>;
    t5 = [sheet, openSheet, hideSheet];
    $[6] = isOpen;
    $[7] = memoizedOpts;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5 as const;
};
interface Props extends UseModalOpts {
  disableAutoFocus?: boolean;
  isOpen: boolean;
  onClose: () => void;
}
function Sheet(props: PropsWithChildren<Props>): JSX.Element {
  const {
    t
  } = useTranslation('core');
  const {
    onClose,
    header,
    isOpen,
    snapPoints,
    children,
    withCloseIcon,
    title,
    onCloseCallback,
    onCloseEnd,
    disableAutoClose,
    disableAutoFocus,
    closeOnSelect
  } = props;
  const [currentSnapIndex, setCurrentSnapIndex] = useState(1);
  const ref = React.useRef<SheetRef | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [sheetIsOpening, setSheetIsOpening] = useState<boolean>(false);
  const onCloseSheet = useCallback(() => {
    if (disableAutoClose) {
      ref.current?.snapTo(1);
    } else {
      onCloseCallback?.();
      onClose();
    }
  }, [disableAutoClose, onClose, onCloseCallback]);
  useKey('Escape', event => {
    event.stopPropagation();
    onCloseSheet();
  }, undefined, []);
  const sholdDisableAutoClose = Boolean(disableAutoClose);
  const isHidden = currentSnapIndex === 1;
  return <SheetComponent ref={ref} isOpen={isOpen} onClose={onCloseSheet} onCloseEnd={onCloseEnd} snapPoints={snapPoints} onCloseStart={() => {
    setSheetIsOpening(false);
  }} onOpenStart={() => {
    setSheetIsOpening(true);
  }} onOpenEnd={() => {
    const availableRef = headerRef.current || contentRef.current;
    if (!disableAutoFocus) {
      availableRef?.focus();
    }
  }} onSnap={setCurrentSnapIndex}>
			<SheetComponent.Container>
				<SheetComponent.Header />
				{header && <div ref={headerRef} tabIndex={-1} aria-label="Navigace" className="relative px-4 pt-0 pb-4">
						{header}
						{withCloseIcon && <button type="button" className="absolute top-0 right-3 flex h-6 w-6 items-center justify-center text-gray-500" onClick={onCloseSheet} aria-label={t('close')}>
								<CloseIcon aria-hidden="true" />
							</button>}
					</div>}

				<SheetComponent.Content style={{
        // @ts-expect-error MotionStyle accept custom css vars, but types not: https://github.com/framer/motion/issues/966
        '--y-sheet-offset': ref.current?.y,
        '--isOpen-sheet': sheetIsOpening ? 'block' : 'none'
      }} disableDrag onScroll={(el: UIEvent<HTMLDivElement>) => {
        el.currentTarget.style.setProperty('--y-sheet-content-scroll-position', `${el.currentTarget.scrollTop}px`);
      }}>
					<div ref={contentRef} tabIndex={-2} aria-label="Detail" data-clarity-mask="true">
						{title && <h2 className="ml-4 text-sm font-semibold text-gray-600 uppercase">{title}</h2>}

						{!header && withCloseIcon && <button type="button" className="absolute top-0 right-3 flex h-6 w-6 items-center justify-center text-gray-500" onClick={onCloseSheet} aria-label={t('close')}>
								<CloseIcon aria-hidden="true" />
							</button>}
						<div className="flex h-full grow flex-col bg-white">
							{closeOnSelect ? Children.map(children, item => {
              if (isValidElement<{
                onClick?: () => void;
              }>(item)) {
                return cloneElement(item, {
                  onClick: () => {
                    item.props.onClick?.();
                    onCloseSheet();
                  }
                });
              }
              return item;
            }) : children}
						</div>
					</div>
				</SheetComponent.Content>
			</SheetComponent.Container>

			<div className={mergeClasses({
      '[&>.react-modal-sheet-backdrop]:translate-y-full': sholdDisableAutoClose && isHidden
    }, {
      '[&>.react-modal-sheet-backdrop]:translate-y-0': sholdDisableAutoClose && !isHidden
    })}>
				<SheetComponent.Backdrop onTap={onCloseSheet} />
			</div>
		</SheetComponent>;
}